import { useState, type FormEvent } from 'react'
import { WORKER_LIMITS_DEFAULT, WORKER_MINUTES_MOST, WORKER_TOKENS_MOST, type Project, type WorkerLimits } from '../api/types'
import { useMounted } from '../components/layout'
import { needsServer } from '../components/needsServer'
import { toast } from '../components/toasts'
import { useUnsavedChanges } from '../components/unsaved'
import { useBoardState, useStore } from '../store/context'
import { sameWorkerLimits, validWorkerLimits } from './workerLimits'
import { settingsWords } from './words'

const words = settingsWords.workers
type Field = keyof WorkerLimits
type Draft = Record<Field, string>
const FIELDS: [Field, string, number][] = [
  ['working_minutes', words.working, WORKER_MINUTES_MOST],
  ['lifetime_minutes', words.lifetime, WORKER_MINUTES_MOST],
  ['token_warning', words.tokens, WORKER_TOKENS_MOST],
]
const draftOf = (limits: WorkerLimits): Draft => ({ working_minutes: String(limits.working_minutes), lifetime_minutes: String(limits.lifetime_minutes), token_warning: String(limits.token_warning) })
/** The draft's limits, or null when a field is not a whole number. */
function limitsOf(draft: Draft): WorkerLimits | null {
  const read = (text: string) => (/^\d+$/.test(text.trim()) ? Number(text.trim()) : NaN)
  const limits = { working_minutes: read(draft.working_minutes), lifetime_minutes: read(draft.lifetime_minutes), token_warning: read(draft.token_warning) }
  return validWorkerLimits(limits) ? limits : null
}

/**
 * A project's default worker limits (Poulpe-486, `set_worker_limits`): a
 * person's to set, or to clear for Poulpe's own. The save names the limits
 * the person read, so the server refuses one made over another's change.
 */
export function WorkerLimitsSettings({ project }: { project: Project }) {
  const store = useStore()
  const { offline } = useBoardState()
  const mounted = useMounted()
  // Cached projects from before this setting existed have no field.
  const saved = project.worker_limits ?? null
  // undefined: no edit; null: back to Poulpe's own; else the fields typed.
  const [edit, setEdit] = useState<Draft | null | undefined>(undefined)
  // What the person read as they began to edit: the save names it, so another's change since is refused, not overwritten.
  const [read, setRead] = useState<WorkerLimits | null>(saved)
  const begin = (next: Draft | null | undefined) => { if (edit === undefined) setRead(saved); setEdit(next); setProblem(null) }
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const draft = edit ?? draftOf(edit === null ? WORKER_LIMITS_DEFAULT : saved ?? WORKER_LIMITS_DEFAULT)
  const typed = edit === undefined ? saved : edit === null ? null : limitsOf(edit)
  // Typing Poulpe's own values over none changes nothing: none stays.
  const dirty = edit === null ? saved !== null : edit !== undefined && (typed === null || !sameWorkerLimits(typed, saved ?? WORKER_LIMITS_DEFAULT))
  const builtIn = edit === null || (edit === undefined && saved === null)
  useUnsavedChanges(dirty)

  async function save(event: FormEvent) {
    event.preventDefault()
    if (store.getState().offline || busy || !dirty) return
    if (edit && !typed) { setProblem(words.wrong); return }
    setBusy(true); setProblem(null)
    const outcome = await store.setWorkerLimits(project.id, read, typed)
    if (!mounted.current) return
    setBusy(false)
    if (outcome.ok) { setEdit(undefined); toast(settingsWords.saved) }
    else setProblem(outcome.message)
  }
  return <section className="settings-section">
    <h2 className="settings-title">{words.title}</h2>
    <p className="muted">{words.about}</p>
    <form className="setup-form" aria-label={words.title} noValidate onSubmit={event => void save(event)}>
      {FIELDS.map(([field, label, most]) => <label key={field} className="field"><span>{label}</span>
        <input type="number" inputMode="numeric" min={1} max={most} step={1} disabled={busy} value={draft[field]}
          onChange={event => begin({ ...draft, [field]: event.target.value })} />
      </label>)}
      {builtIn && <p className="muted">{words.builtIn}</p>}
      {problem && <p role="alert" className="text-danger">{problem}</p>}
      <div className="form-actions">
        <button className="button button-primary" disabled={offline || busy || !dirty} {...needsServer(offline)}>{busy ? words.saving : words.save}</button>
        {!builtIn && <button type="button" className="button button-quiet" disabled={busy} onClick={() => begin(saved === null ? undefined : null)}>{words.reset}</button>}
      </div>
    </form>
  </section>
}
